import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import AdminLayout from "../layouts/AdminLayout";
import { Guest } from "../types/guest";
import { apiService } from "../services/api";
import Loader from "../components/Loader";
import {
  Users,
  Search,
  Mail,
  Phone,
  Calendar,
  DollarSign,
  AlertCircle,
  ArrowUpRight,
} from "lucide-react";

const Customers: React.FC = () => {
  const navigate = useNavigate();
  const [customers, setCustomers] = useState<Guest[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const today = new Date();
  const lastMonth = new Date();
  lastMonth.setMonth(today.getMonth() - 1);

  const formatDate = (date: Date) => date.toISOString().split("T")[0];

  const [startDate, setStartDate] = useState(formatDate(lastMonth));
  const [endDate, setEndDate] = useState(formatDate(today));
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  const indexOfLastCustomer = currentPage * itemsPerPage;
  const indexOfFirstCustomer = indexOfLastCustomer - itemsPerPage;

  const currentCustomers = customers.slice(
    indexOfFirstCustomer,
    indexOfLastCustomer,
  );

  const totalPages = Math.ceil(customers.length / itemsPerPage);

  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState<Guest | null>(null);

  const handleViewClick = (customer: Guest) => {
    setSelectedCustomer(customer);
    setIsEditModalOpen(true);
  };

  useEffect(() => {
    fetchCustomers(startDate, endDate, searchTerm);
  }, []);

  const fetchCustomers = async (
    start?: string,
    end?: string,
    search?: string,
  ) => {
    setIsLoading(true);
    setError("");

    try {
      const response = await apiService.getCustomers({
        startDate: start,
        endDate: end,
        search: search,
      });

      setCustomers(response.data);
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "Failed to fetch customers",
      );
    } finally {
      setIsLoading(false);
    }
  };

  const updateCustomer = async (
    id: string,
    data: { name: string; phone: string; email: string },
  ) => {
    try {
      await apiService.updateCustomers(id, data);
      fetchCustomers(startDate, endDate, searchTerm);
      alert("Customer updated successfully");
    } catch (err) {
      console.error(err);
      alert("Failed to update customer");
    }
  };

  useEffect(() => {
    const delay = setTimeout(() => {
      fetchCustomers(startDate, endDate, searchTerm);
    }, 400); // debounce

    return () => clearTimeout(delay);
  }, [searchTerm]);

  // const filteredCustomers = customers.filter(
  //   (customer) =>
  //     `${customer.name} `.toLowerCase().includes(searchTerm.toLowerCase()) ||
  //     customer.email.toLowerCase().includes(searchTerm.toLowerCase())
  // );

  const totalSpent = customers.reduce((sum, g) => sum + g.totalSpent, 0);
  const totalBookings = customers.reduce((sum, g) => sum + g.totalBookings, 0);

  return (
    <AdminLayout>
      <div>
        {/* Header */}
        <div className="mb-6">
          <h1 className="text-2xl font-serif font-bold text-sage-900">
            Customers
          </h1>
          <p className="text-sm text-sage-500 mt-1">
            View and manage all registered platform customers
          </p>
        </div>
        <div className="flex items-center gap-2 mb-4"></div>

        {/* Search */}
        {/* <div className="mb-6">
          <div className="relative">
            <Search size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-sage-400" />
            <input
              type="text"
              placeholder="Search by name or email..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2.5 bg-white border border-beige-300 rounded-xl text-sage-900 placeholder-sage-400 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500/20 focus:border-brand-500 transition-colors"
            />
          </div>
        </div> */}

        {/* Error */}
        {error && (
          <div className="mb-6 p-3.5 bg-red-50 border border-red-200 rounded-xl flex items-start gap-3">
            <AlertCircle
              size={18}
              className="text-red-500 mt-0.5 flex-shrink-0"
            />
            <p className="text-sm text-red-700">{error}</p>
          </div>
        )}

        {/* Date Range Filter */}
        {/* <div className="mb-6 flex flex-col md:flex-row gap-3">

          <div className="flex items-center gap-2">
            <Calendar size={16} className="text-sage-400" />

            <input
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              className="border border-beige-300 rounded-lg px-3 py-2 text-sm"
            />
          </div>

          <div className="flex items-center gap-2">

            <input
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              className="border border-beige-300 rounded-lg px-3 py-2 text-sm"
            />
          </div>

          <button
            onClick={() => fetchCustomers(startDate, endDate)}
            className="px-4 py-2 bg-brand-600 text-white rounded-lg text-sm hover:bg-brand-700"
          >
            Filter
          </button>

          <button
            onClick={() => {
              setStartDate('');
              setEndDate('');
              fetchCustomers();
            }}
            className="px-4 py-2 border border-beige-300 rounded-lg text-sm"
          >
            Reset
          </button>

        </div> */}

        {/* Filters */}
        <div className="mb-6 bg-white border border-beige-300 rounded-xl p-4">
          <div className="flex flex-col md:flex-row md:items-center gap-3">
            {/* Search */}
            <div className="relative flex-1">
              <Search
                size={16}
                className="absolute left-3 top-1/2 -translate-y-1/2 text-sage-400"
              />

              <input
                type="text"
                placeholder="Search guest..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-9 pr-3 py-2 border border-beige-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-500/20"
              />
            </div>

            {/* Start Date */}
            <div className="flex items-center gap-2">
              <Calendar size={16} className="text-sage-400" />

              <input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                className="border border-beige-300 rounded-lg px-3 py-2 text-sm"
              />
            </div>

            {/* End Date */}
            <div className="flex items-center gap-2">
              <input
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                className="border border-beige-300 rounded-lg px-3 py-2 text-sm"
              />
            </div>

            {/* Apply Filter */}
            <button
              onClick={() => fetchCustomers(startDate, endDate, searchTerm)}
              className="px-4 py-2 bg-brand-600 text-white rounded-lg text-sm hover:bg-brand-700 transition"
            >
              Apply
            </button>

            {/* Reset */}
            <button
              onClick={() => {
                setSearchTerm("");
                setStartDate(formatDate(lastMonth));
                setEndDate(formatDate(today));
                fetchCustomers(startDate, endDate, searchTerm);
              }}
              className="px-4 py-2 border border-beige-300 rounded-lg text-sm hover:bg-beige-50"
            >
              Reset
            </button>
          </div>
        </div>

        {/* Record per page */}
        <div className="flex items-center gap-2 mb-4">
          <select
            value={itemsPerPage}
            onChange={(e) => {
              setItemsPerPage(Number(e.target.value));
              setCurrentPage(1);
            }}
            className="border border-gray-300 rounded px-2 py-1 text-sm"
          >
            <option value={10}>10</option>
            <option value={25}>25</option>
            <option value={50}>50</option>
            <option value={100}>100</option>
          </select>

          <span className="text-sm font-medium text-green-700">
            records per page
          </span>
        </div>

        {/* Customers */}
        {isLoading ? (
          <Loader fullScreen />
        ) : customers.length === 0 ? (
          <div className="bg-white rounded-xl border border-beige-300 p-12 text-center">
            <div className="w-16 h-16 bg-beige-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
              <Users size={24} className="text-sage-400" />
            </div>
            <p className="text-sage-900 text-base font-semibold mb-1">
              No customers found
            </p>
            <p className="text-sage-500 text-sm">
              {searchTerm
                ? "Try adjusting your search criteria"
                : "No registered customers yet"}
            </p>
          </div>
        ) : (
          <>
            {/* Desktop Table */}
            <div className="hidden md:block bg-white rounded-xl border border-beige-300 overflow-hidden">
              <table className="w-full">
                <thead>
                  <tr className="bg-beige-50 border-b border-beige-200">
                    <th className="text-left px-5 py-3 text-xs font-semibold text-sage-500 uppercase tracking-wider">
                      Customer
                    </th>
                    <th className="text-left px-5 py-3 text-xs font-semibold text-sage-500 uppercase tracking-wider">
                      Contact
                    </th>
                    <th className="text-left px-5 py-3 text-xs font-semibold text-sage-500 uppercase tracking-wider">
                      Email
                    </th>
                    <th className="text-left px-5 py-3 text-xs font-semibold text-sage-500 uppercase tracking-wider">
                      Member Since
                    </th>
                    <th className="text-right px-5 py-3 text-xs font-semibold text-sage-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-beige-200">
                  {currentCustomers.map((customer) => (
                    <tr
                      key={customer.id}
                      className="hover:bg-beige-50/60 transition-colors"
                    >
                      <td className="px-5 py-4">
                        <div className="flex items-center gap-3">
                          <div className="w-9 h-9 rounded-full bg-brand-50 flex items-center justify-center text-brand-600 font-semibold text-xs flex-shrink-0">
                            {customer?.name?.trim()?.charAt(0)?.toUpperCase() ||
                              "?"}
                          </div>
                          <div className="min-w-0">
                            <p className="text-sm font-medium text-sage-900">
                              {customer.name}
                            </p>
                            <p className="text-xs text-sage-500">
                              {customer.nationality || "Tanzania"}
                            </p>
                          </div>
                        </div>
                      </td>
                      <td className="px-5 py-4">
                        <div className="flex flex-col gap-1">
                          <div className="flex items-center gap-1.5 text-xs text-sage-600">
                            <Phone size={12} className="text-sage-400" />
                            <span>{customer.phone}</span>
                          </div>
                        </div>
                      </td>
                      <td className="px-5 py-4">
                        <div className="flex flex-col gap-1">
                          <div className="flex items-center gap-1.5 text-xs text-sage-600">
                            <Mail size={12} className="text-sage-400" />
                            <span className="truncate max-w-[180px]">
                              {customer?.email || "N/A"}
                            </span>
                          </div>
                        </div>
                      </td>
                      <td className="px-5 py-4">
                        <p className="text-sm text-sage-600">
                          {new Date(customer.createdAt).toLocaleDateString()}
                        </p>
                      </td>
                      <td className="px-5 py-4 text-right">
                        <button
                          onClick={() => handleViewClick(customer)}
                          className="inline-flex items-center gap-1 text-sm font-medium text-brand-500 hover:text-brand-600 transition-colors"
                        >
                          View
                          <ArrowUpRight size={13} />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Mobile Cards */}
            <div className="md:hidden space-y-3">
              {customers.map((customer) => (
                <div
                  key={customer.id}
                  onClick={() => navigate(`/customers/${customer.id}`)}
                  className="bg-white rounded-xl border border-beige-300 p-4 hover:shadow-card-hover transition-shadow cursor-pointer"
                >
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex items-center gap-3">
                      <div className="w-9 h-9 rounded-full bg-brand-50 flex items-center justify-center text-brand-600 font-semibold text-xs flex-shrink-0">
                        {customer?.name?.trim()?.charAt(0)?.toUpperCase() ||
                          "?"}
                      </div>
                      <div>
                        <p className="text-sm font-medium text-sage-900">
                          {customer.name}
                        </p>
                        <p className="text-xs text-sage-500">
                          {customer?.nationality || "Tanzania"}
                        </p>
                      </div>
                    </div>
                    <span className="inline-flex items-center px-2 py-0.5 bg-brand-50 text-brand-600 rounded-full text-xs font-medium">
                      {customer.totalBookings} bookings
                    </span>
                  </div>

                  <div className="flex flex-col gap-1.5 mb-3 text-xs">
                    <div className="flex items-center gap-2 text-sage-600">
                      <Mail size={12} className="text-sage-400 flex-shrink-0" />
                      <span className="truncate">{customer.email}</span>
                    </div>
                    <div className="flex items-center gap-2 text-sage-600">
                      <Phone
                        size={12}
                        className="text-sage-400 flex-shrink-0"
                      />
                      <span>{customer.phone}</span>
                    </div>
                  </div>

                  {/* <div className="flex items-center justify-between pt-3 border-t border-beige-200">
                    <div>
                      <p className="text-xs text-sage-400">Total Spent</p>
                      <p className="text-sm font-semibold text-sage-900">${customer.totalSpent.toLocaleString()}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-xs text-sage-400">Member Since</p>
                      <p className="text-sm text-sage-600">{new Date(customer.memberSince).toLocaleDateString()}</p>
                    </div>
                  </div> */}
                </div>
              ))}
            </div>
            {totalPages > 1 && (
              <div className="flex justify-center items-center gap-2 mt-6">
                <button
                  onClick={() => setCurrentPage((p) => Math.max(p - 1, 1))}
                  disabled={currentPage === 1}
                  className="px-3 py-1 border rounded-lg text-sm disabled:opacity-50"
                >
                  Prev
                </button>

                {Array.from({ length: totalPages }, (_, i) => (
                  <button
                    key={i}
                    onClick={() => setCurrentPage(i + 1)}
                    className={`px-3 py-1 border rounded-lg text-sm ${
                      currentPage === i + 1
                        ? "bg-brand-600 text-white"
                        : "bg-white"
                    }`}
                  >
                    {i + 1}
                  </button>
                ))}

                <button
                  onClick={() =>
                    setCurrentPage((p) => Math.min(p + 1, totalPages))
                  }
                  disabled={currentPage === totalPages}
                  className="px-3 py-1 border rounded-lg text-sm disabled:opacity-50"
                >
                  Next
                </button>
              </div>
            )}
          </>
        )}
        {isEditModalOpen && selectedCustomer && (
          <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
            <div className="bg-white rounded-xl p-6 w-full max-w-md">
              <h2 className="text-lg font-semibold mb-4">Edit Customer</h2>

              <div className="flex flex-col gap-3">
                <input
                  type="text"
                  placeholder="Name"
                  value={selectedCustomer.name}
                  onChange={(e) =>
                    setSelectedCustomer({
                      ...selectedCustomer,
                      name: e.target.value,
                    })
                  }
                  className="border rounded px-3 py-2"
                />
                <input
                  type="text"
                  placeholder="Phone"
                  value={selectedCustomer.phone}
                  onChange={(e) =>
                    setSelectedCustomer({
                      ...selectedCustomer,
                      phone: e.target.value.replace(/\D/g, "").slice(0, 9),
                    })
                  }
                  className="border rounded px-3 py-2"
                />

                <input
                  type="email"
                  placeholder="Email"
                  value={selectedCustomer.email}
                  onChange={(e) =>
                    setSelectedCustomer({
                      ...selectedCustomer,
                      email: e.target.value,
                    })
                  }
                  className="border rounded px-3 py-2"
                />
              </div>

              <div className="flex justify-end gap-3 mt-4">
                <button
                  onClick={() => setIsEditModalOpen(false)}
                  className="px-4 py-2 rounded-lg border"
                >
                  Cancel
                </button>
                <button
                  onClick={() => {
                    if (selectedCustomer) {
                      updateCustomer(selectedCustomer.id, {
                        name: selectedCustomer.name ?? "",
                        phone: selectedCustomer.phone ?? "",
                        email: selectedCustomer.email ?? "",
                      });
                      setIsEditModalOpen(false);
                    }
                  }}
                  className="px-4 py-2 rounded-lg bg-brand-600 text-white hover:bg-brand-700"
                >
                  {/* Save */}
                  Update
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </AdminLayout>
  );
};

export default Customers;
